import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import {
  useGetAssetQuery,
  useUpdateAssetMutation,
} from '@/redux/slices/assetApiSlice';
import { MenuItem, Text } from '@electricjs/arc';
import { AssetStatus } from '@electricjs/core_entity-client';
import { useTranslation } from 'react-i18next';

type ArchiveMenuItemProps = {
  assetId: string;
  setIsloading: (_: boolean) => void;
};

const ArchiveAssetMenuItem = ({
  assetId,
  setIsloading,
}: ArchiveMenuItemProps) => {
  const { t } = useTranslation('assets');
  const { showSuccessToast, showErrorToast } = useGlobalUI();
  const organizationId = useGetOrganizationId();
  const { assetStatus, isFetching } = useGetAssetQuery(
    {
      organizationId,
      assetId,
    },
    {
      skip: !assetId,
      selectFromResult: ({ data, isFetching }) => ({
        assetStatus: data?.status,
        isFetching,
      }),
    }
  );
  const [updateAsset, { isLoading: isUpdating }] = useUpdateAssetMutation();

  const disableAction = isUpdating || isFetching;

  const handleClick = async () => {
    const newStatus =
      assetStatus === AssetStatus.Archived
        ? AssetStatus.Available
        : AssetStatus.Archived;
    const assetAction =
      assetStatus === AssetStatus.Archived ? 'unarchive' : 'archive';

    setIsloading(true);

    updateAsset({
      assetId,
      organizationId,
      status: newStatus,
    })
      .then(() => {
        showSuccessToast({
          id: `${assetAction}-asset-success-toast`,
          message:
            newStatus === AssetStatus.Archived
              ? t('assetDetailsButtonMenu.archiveSuccess')
              : t('assetDetailsButtonMenu.unarchiveSuccess'),
        });
      })
      .catch(() => {
        showErrorToast({
          id: `${assetAction}-asset-error-toast`,
          message:
            assetStatus === AssetStatus.Archived
              ? t('assetDetailsButtonMenu.unarchiveError')
              : t('assetDetailsButtonMenu.archiveError'),
        });
      })
      .finally(() => {
        setIsloading(false);
      });
  };

  if (assetStatus === AssetStatus.Archived) {
    return (
      <MenuItem
        id="unarchive-asset-menu-item"
        onClick={handleClick}
        disabled={disableAction}>
        <Text>{t('assetDetailsButtonMenu.unarchive')}</Text>
      </MenuItem>
    );
  }

  return (
    <MenuItem
      id="archive-asset-menu-item"
      onClick={handleClick}
      disabled={disableAction}>
      <Text>{t('assetDetailsButtonMenu.archive')}</Text>
    </MenuItem>
  );
};

export default ArchiveAssetMenuItem;
